import { useState, useEffect } from 'react';
import {
  List, Accordion, AccordionSummary, AccordionDetails, ListItemText, Typography,
  Chip, Divider, Box, Stack, TextField, InputAdornment, Select, MenuItem, FormControl,
  Grid, Avatar, IconButton, InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingAnimation from './LoadingAnimation';
import FloatingActionButton from './FloatingActionButton';
import AddReviewModal from './AddReviewModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
// import { fetchClients, fetchMails, fetchReviews, addReview, uploadReviewImage, deleteReview } from '../api/github';
import { fetchReviews, addReview, uploadReviewImage, deleteReview } from '../api/github';

const formatDateTime = (dateString) => {
  if (!dateString) return 'Йозилмаган';
  const date = new Date(dateString);
  return date.toLocaleString();
};

export default function ReviewsList({ isMobile, refreshTrigger }) {
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('review');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);

  useEffect(() => {
    const loadReviews = async () => {
      setLoading(true);
      try {
        // fetchClients(),
        // fetchMails(localStorage.getItem('userId')),
        const [newReviews] = await Promise.all([
          fetchReviews()
        ]);

        // flatMap(client =>
        //   client.orders
        //     .filter(order => order.review)
        //     .map(order => ({
        //       id: `client_${client.id}_${order.id}`,
        //       review: order.review,
        //       employeeName: order.employeeName,
        //       nickname: client.nickname,
        //       date: order.date,
        //       source: 'client',
        //       imageUrl: null
        //     }))
        // );

        // const mailReviews = mails
        //   .filter(mail => mail.review)
        //   .map(mail => ({
        //     id: `mail_${mail.id}`,
        //     review: mail.review,
        //     employeeName: mail.employeeName,
        //     nickname: mail.nickname,
        //     date: mail.createdAt,
        //     source: 'mail',
        //     imageUrl: null
        //   }));

        const formattedNewReviews = newReviews.map(review => ({
          id: review.id,
          review: review.review,
          employeeName: review.employeeName,
          nickname: review.nickname,
          code: review.code,
          date: review.createdAt,
          source: review.source,
          imageUrl: review.imageUrl || null
        }));

        setReviews([...formattedNewReviews]);
      } catch (error) {
        console.error('Отзывлар юкланишда хатолик:', error);
        alert(error.message || 'Отзывлар юкланишда хатолик');
      } finally {
        setLoading(false);
      }
    };
    loadReviews();
  }, [refreshTrigger]);

  const handleAddReview = async ({ review, nickname, code, source, image }) => {
    setIsSubmitting(true);
    try {
      let imageUrl = null;
      if (image) {
        imageUrl = await uploadReviewImage(image);
      }

      const reviewData = {
        review,
        nickname,
        code,
        source,
        imageUrl
      };

      const newReviewResponse = await addReview(reviewData);
      setReviews(prev => [
        ...prev,
        {
          id: newReviewResponse.id,
          review: newReviewResponse.review,
          employeeName: localStorage.getItem('userName'),
          nickname: newReviewResponse.nickname,
          code: newReviewResponse.code,
          date: newReviewResponse.createdAt,
          source: newReviewResponse.source,
          imageUrl: newReviewResponse.imageUrl
        }
      ]);

      setOpenAddModal(false);
    } catch (error) {
      console.error('Отзыв қўшишда хатолик:', error);
      alert(error.message || 'Отзыв қўшишда хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!reviewToDelete) return;

    setIsSubmitting(true);
    try {
      await deleteReview(reviewToDelete);
      setReviews(prev => prev.filter(review => review.id !== reviewToDelete));
      setDeleteDialogOpen(false);
      setReviewToDelete(null);
    } catch (error) {
      console.error('Отзыв ўчиришда хатолик:', error);
      alert(error.message || 'Отзыв ўчиришда хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch =
      filterField === 'review' && review.review.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'nickname' && review.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'employee' && review.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'code' && review.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'date' && formatDateTime(review.date).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSource =
      sourceFilter === 'all' ||
      (sourceFilter === 'client' && review.source === 'client') ||
      (sourceFilter === 'mail' && review.source === 'mail');

    return matchesSearch && matchesSource;
  });

  return (
    <Box>
      {loading ? (
        <LoadingAnimation loading={loading} />
      ) : reviews.length === 0 ? (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Отзывлар мавжуд эмас
        </Typography>
      ) : (
        <List>
          <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Фильтр по</InputLabel>
              <Select
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
                label="Фильтр по"
              >
                <MenuItem value="review">Отзыв</MenuItem>
                <MenuItem value="nickname">Ник</MenuItem>
                <MenuItem value="employee">Ходим</MenuItem>
                <MenuItem value="code">Код</MenuItem>
                <MenuItem value="date">Сана</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Қидириш..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
              size="small"
            />
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Манба</InputLabel>
              <Select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                label="Манба"
              >
                <MenuItem value="all">Ҳаммаси</MenuItem>
                <MenuItem value="client">Мижозлар</MenuItem>
                <MenuItem value="mail">Почта</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {filteredReviews.map(review => (
            <Accordion key={review.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <ListItemText
                  primary={review.review.length > 50 ? `${review.review.substring(0, 50)}...` : review.review}
                  secondary={`Ходим: ${review.employeeName}`}
                />
                <Chip
                  label={review.nickname}
                  sx={{
                    ml: 2,
                    mr: 1,
                    color: 'white',
                    backgroundColor: review.source === 'client' ? 'blue' : 'purple'
                  }}
                />
                <IconButton
                  sx={{ color: 'error.main' }}
                  onClick={() => {
                    setReviewToDelete(review.id);
                    setDeleteDialogOpen(true);
                  }}
                  disabled={isSubmitting}
                >
                  <DeleteIcon />
                </IconButton>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" alignItems="center" gap={1} mb={2}>
                    <ReviewsIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2">Отзыв маълумотлари</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {review.imageUrl && (
                      <Grid item xs={12} sm={4}>
                        <Avatar
                          src={review.imageUrl}
                          alt="Отзыв расми"
                          sx={{ width: '100%', maxWidth: 150, height: 150, borderRadius: 1 }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={review.imageUrl ? 8 : 12}>
                      <Typography variant="body2" gutterBottom>
                        Мижоз: {review.nickname}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Код: {review.code}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Сана: {formatDateTime(review.date)}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body1">
                        {review.review}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </List>
      )}
      <FloatingActionButton
        onClick={() => setOpenAddModal(true)}
        tooltip="Янги отзыв қўшиш"
      />
      <AddReviewModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onAddReview={handleAddReview}
        isSubmitting={isSubmitting}
      />
      <ConfirmDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteReview}
        itemName="Отзыв"
        title="Отзывни ўчириш"
        message="Хақиқатан хам бу отзывни ўчирмоқчимисиз?"
        confirmText="Ҳа"
        cancelText="Йўқ"
      />
    </Box>
  );
}